import type {FC, JSX} from 'react'
import {useState, useEffect, useRef} from 'react'
import {Image} from './image'

interface BackToTopButtonProps {
	altText: string
	hide: boolean
}

interface ToTopButton {
	buttonImage?: string
	altText?: string
}

interface DefaultBackButtonProps {
	altText: string
}

const DefaultBackButton = ({altText}: DefaultBackButtonProps): JSX.Element => (
	<svg
		height={32}
		viewBox="0 0 32 32"
		width={32}
		xmlns="http://www.w3.org/2000/svg"
	>
		<title>{altText}</title>
		<g data-name="Group 1" id="Group_1" transform="translate(-192 -143)">
			<rect
				data-name="Rectangle 5301"
				height={32}
				id="Rectangle_5301"
				rx={6}
				transform="translate(192 143)"
				width={32}
			/>
			<path
				d="M12.885,22.67l6.074-6.088L12.885,10.5l1.87-1.87,7.958,7.958L14.755,24.54Z"
				data-name="Icon material-keyboard-arrow-right"
				fill="#fff"
				id="Icon_material-keyboard-arrow-right"
				transform="translate(191.375 176.713) rotate(-90)"
			/>
		</g>
	</svg>
)

const ToTopButton = ({
	buttonImage,
	altText = 'Go back to top of page',
}: ToTopButton): JSX.Element => {
	const [scrolled, setScrolled] = useState(0)
	const [offsetY, setOffsetY] = useState(0)
	const buttonRef = useRef<HTMLButtonElement>(null)

	const handleClick = (): void => {
		window.scrollTo({top: 0, behavior: 'smooth'})
	}
	const handleScroll = (): void => {
		setScrolled(window.scrollY)
	}
	const standardOffsetFromBottomPx = 14
	const minimumScrollPx = 20

	useEffect(() => {
		const spacerNode =
			document.getElementById('sticky_bottom_nav_spacer') ||
			document.getElementById('our_sticky_bottom_nav_spacer')
		if (spacerNode) {
			const resizeObserver = new ResizeObserver((entries) => {
				const rect = entries[0]?.contentRect
				if (rect) {
					const {height} = rect
					if (height !== offsetY) setOffsetY(height)
				}
			})
			resizeObserver.observe(spacerNode)
		}

		window.addEventListener('scroll', handleScroll, {passive: true})
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [offsetY])

	useEffect(() => {
		if (buttonRef.current) {
			buttonRef.current.style.bottom = `${
				standardOffsetFromBottomPx + offsetY
			}px`
		}
	}, [offsetY])

	return (
		<button
			className={`fixed right-4 h-8 w-8 fill-blue-500 transition duration-500 ${
				scrolled > minimumScrollPx
					? 'visible opacity-70 hover:opacity-100'
					: 'invisible opacity-0'
			}`}
			onClick={handleClick}
			ref={buttonRef}
			type="button"
		>
			{buttonImage ? (
				<Image alt={altText} height={32} src={buttonImage} width={32} />
			) : (
				<DefaultBackButton altText={altText} />
			)}
		</button>
	)
}

export const BackToTopButton: FC<BackToTopButtonProps> = ({
	altText = '',
	hide,
}) => (!hide ? <ToTopButton altText={altText} /> : null)
